import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Button,
  Chip,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useListAllSubscriptionsSuperAdmin } from "../../hooks/api/useSubscription";
import DateRangeSelector from "../common/DateRangeSelector";
import AdminScopeFilters, {
  ALL_BUSINESS_TYPES,
  ALL_COUNTRIES,
  AdminScopeFilterLegend,
} from "../common/AdminScopeFilters";
import AdminActiveFilterChips from "../common/AdminActiveFilterChips";
import AdminPageShell from "../common/AdminPageShell";
import AdminFilterBar from "../common/AdminFilterBar";
import AdminSearchField from "../common/AdminSearchField";
import AdminDataTable, {
  type AdminTableColumn,
} from "../common/AdminDataTable";
import { formatAdminMoney } from "../common/adminMarketCountries";
import {
  formatSubscriptionStatusLabel,
  SUBSCRIPTION_STATUS_FILTER_OPTIONS,
} from "../common/adminEnumLabels";
import type { Subscription } from "../../Graphql/types/subscription";
import { getSubscriptionDetailPath } from "../../constants/routes";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

function subscriptionStatusSx(status: string) {
  const map: Record<string, { bgcolor: string; color: string }> = {
    ACTIVE: { bgcolor: "success.light", color: "success.dark" },
    PAUSED: { bgcolor: "warning.light", color: "warning.dark" },
    PENDING: { bgcolor: "info.light", color: "info.dark" },
  };
  return map[status] ?? { bgcolor: "grey.200", color: "text.secondary" };
}

const SubscriptionListTableView: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<Subscription[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);
  const [countryCode, setCountryCode] = useState(ALL_COUNTRIES);
  const [businessType, setBusinessType] = useState(ALL_BUSINESS_TYPES);

  const listAll = useListAllSubscriptionsSuperAdmin();

  const loadSubscriptions = useCallback(() => {
    listAll
      .mutateAsync({
        page,
        size: rowsPerPage,
        status: statusFilter || undefined,
        search: search || undefined,
        dateFrom: dateRange?.startDate?.toISOString(),
        dateTo: dateRange?.endDate?.toISOString(),
        countryCode: countryCode || undefined,
        businessType: businessType || undefined,
      })
      .then((res) => {
        const data = res.listAllSubscriptionsSuperAdmin;
        setList(data?.subscriptionList ?? []);
        setTotalElements(data?.totalElements ?? 0);
      })
      .catch(() => {});
  }, [
    page,
    rowsPerPage,
    statusFilter,
    search,
    dateRange,
    countryCode,
    businessType,
  ]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearchSubmit = () => {
    setSearch(searchInput.trim());
    setPage(0);
  };

  const columns: AdminTableColumn<Subscription>[] = [
    {
      id: "subscriptionId",
      header: "Subscription ID",
      mobilePrimary: true,
      renderCell: (sub) => (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.8125rem">
          {sub.subscriptionId}
        </Typography>
      ),
    },
    {
      id: "email",
      header: "Company admin email",
      mobileLabel: "Admin email",
      renderCell: (sub) => sub.companyAdminEmail || sub.companyId || "—",
    },
    {
      id: "plan",
      header: "Plan",
      renderCell: (sub) => sub.planName || "—",
    },
    {
      id: "price",
      header: "Price",
      align: "right",
      renderCell: (sub) =>
        sub.priceAmount != null
          ? formatAdminMoney(sub.priceAmount, sub.priceCurrency ?? "RWF")
          : "—",
    },
    {
      id: "status",
      header: "Status",
      renderCell: (sub) => {
        const sx = subscriptionStatusSx(sub.status);
        return (
          <Typography
            component="span"
            variant="body2"
            fontWeight={600}
            sx={{
              px: 1.25,
              py: 0.375,
              borderRadius: 1.5,
              display: "inline-block",
              fontSize: "0.75rem",
              ...sx,
            }}
          >
            {formatSubscriptionStatusLabel(sub.status)}
          </Typography>
        );
      },
    },
  ];

  const isLoading = listAll.isPending && list.length === 0;

  return (
    <AdminPageShell
      title="Subscriptions"
      description={<AdminScopeFilterLegend />}
      meta={
        isLoading
          ? "Loading…"
          : `${totalElements} ${totalElements === 1 ? "subscription" : "subscriptions"}`
      }
      toolbar={
        <AdminFilterBar
          search={
            <AdminSearchField
              placeholder="Subscription or company ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchSubmit();
              }}
            />
          }
          filters={
            <>
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: "100%", sm: 140 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  {SUBSCRIPTION_STATUS_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <AdminScopeFilters
                values={{ countryCode, businessType }}
                onChange={({ countryCode: c, businessType: b }) => {
                  setCountryCode(c);
                  setBusinessType(b);
                  setPage(0);
                }}
                periodControl={
                  <DateRangeSelector
                    compact
                    value={dateRange}
                    onChange={(range) => {
                      setDateRange(range);
                      setPage(0);
                    }}
                  />
                }
              />
            </>
          }
          actions={
            <Button
              variant="contained"
              size="small"
              onClick={handleSearchSubmit}
            >
              Search
            </Button>
          }
        />
      }
      filterChips={
        <AdminActiveFilterChips
          values={{ countryCode, businessType }}
          onClearCountry={() => {
            setCountryCode(ALL_COUNTRIES);
            setPage(0);
          }}
          onClearBusinessType={() => {
            setBusinessType(ALL_BUSINESS_TYPES);
            setPage(0);
          }}
          onClearAll={() => {
            setCountryCode(ALL_COUNTRIES);
            setBusinessType(ALL_BUSINESS_TYPES);
            setPage(0);
          }}
          extra={
            statusFilter ? (
              <Chip
                size="small"
                label={`Status: ${formatSubscriptionStatusLabel(statusFilter)}`}
                onDelete={() => {
                  setStatusFilter("");
                  setPage(0);
                }}
              />
            ) : undefined
          }
        />
      }
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <AdminDataTable
          columns={columns}
          rows={list}
          getRowId={(s) => s.subscriptionId}
          onRowClick={(s) =>
            navigate(getSubscriptionDetailPath(s.subscriptionId))
          }
          emptyMessage="No subscriptions found."
          footer={
            list.length > 0 ? (
              <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                labelRowsPerPage="Rows:"
                sx={{ borderTop: 1, borderColor: "divider" }}
              />
            ) : undefined
          }
        />
      )}
    </AdminPageShell>
  );
};

export default SubscriptionListTableView;
